/**
 * OnboardingActionsStep
 *
 * Third step of onboarding - explain transport controls and saved Actions.
 * Compact design with visual workflow graphic.
 */

import { ListPlus, Play, Route, Sparkles, Square, Zap } from "lucide-react"

// Button styles matching InputBar.tsx
const BUTTON_BASE = "flex items-center justify-center gap-1.5 px-3 h-8 text-sm font-medium whitespace-nowrap"

const BUTTON_STYLES = {
    success: "bg-success text-success-content",
    danger: "bg-error text-error-content",
    ghost: "bg-base-200 text-base-content",
} as const

export const OnboardingActionsStep = () => {
    return (
        <div className="flex flex-col gap-4">
            <div className="text-center">
                <h2 className="text-xl font-bold text-base-content mb-1">Stay in control</h2>
                <p className="text-sm text-muted">Send work now, queue what is next, or build your own shortcuts</p>
            </div>

            <div className="flex flex-col gap-2">
                <div className="flex items-center gap-3 p-3 bg-base-200/50 border border-border">
                    <div className={`${BUTTON_BASE} ${BUTTON_STYLES.success} flex-shrink-0`}>
                        <Play size={14} />
                        Do
                    </div>
                    <div className="flex-1 min-w-0">
                        <span className="text-sm text-base-content">Start a message immediately when the task is idle</span>
                    </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-base-200/50 border border-border">
                    <div className={`${BUTTON_BASE} ${BUTTON_STYLES.success} flex-shrink-0`}>
                        <ListPlus size={14} />
                        Queue
                    </div>
                    <div className="flex-1 min-w-0">
                        <span className="text-sm text-base-content">Line up the next message while the agent is working</span>
                    </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-base-200/50 border border-border">
                    <div className={`${BUTTON_BASE} ${BUTTON_STYLES.ghost} flex-shrink-0`}>
                        <Route size={14} />
                        Steer
                    </div>
                    <div className="flex-1 min-w-0">
                        <span className="text-sm text-base-content">Send guidance into a running turn when the harness supports it</span>
                    </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-base-200/50 border border-border">
                    <div className={`${BUTTON_BASE} ${BUTTON_STYLES.danger} flex-shrink-0`}>
                        <Zap size={14} />
                        Interrupt
                    </div>
                    <div className="flex-1 min-w-0">
                        <span className="text-sm text-base-content">Stop the active turn and send a replacement message</span>
                    </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-base-200/50 border border-border">
                    <div className={`${BUTTON_BASE} ${BUTTON_STYLES.danger} flex-shrink-0`}>
                        <Square size={14} />
                        Stop
                    </div>
                    <div className="flex-1 min-w-0">
                        <span className="text-sm text-base-content">Stop the active turn without sending another message</span>
                    </div>
                </div>
            </div>

            <div className="p-4 bg-primary/10 border border-primary/30">
                <div className="flex items-start gap-3">
                    <div className={`${BUTTON_BASE} ${BUTTON_STYLES.ghost} h-9 flex-shrink-0`}>
                        <Sparkles size={14} />
                        Actions
                    </div>
                    <div>
                        <p className="text-sm font-medium text-base-content">Make common messages one click</p>
                        <p className="mt-1 text-xs leading-5 text-muted">Toggle the live defaults to fit your workflow, or duplicate one to customize it.</p>
                    </div>
                </div>
            </div>
        </div>
    )
}
